/**
 * Chart-specific DuckDB SQL transforms.
 *
 * Each function produces a SQL fragment (SELECT, WHERE, GROUP BY, ORDER BY, or
 * LIMIT clause) that `chartRift` composes into a final query. All transforms
 * operate on an intermediate CTE called `_base` which contains the output of
 * the standard `rift()` pipeline.
 *
 * To add a new transform:
 *   1. Create a pure function here that returns the relevant SQL clause(s).
 *   2. Wire it into `chartRift()` in `chart-rift.ts`.
 *   3. Add tests in `__tests__/chart-rift.test.ts`.
 */

import type { DataType } from "@repo/lanes";

import { dateFormatToTruncUnit } from "../../types/column-format";
import type { GraphType } from "../../types/graph-config";
import { resolveColumnFormat } from "../../utils/merge-columns-configs";
import type { ColumnsConfig, RiftConfig } from "../types";
import { escapeIdentifier } from "./escape-identifier";

// ---------------------------------------------------------------------------
// Default row limits per chart type — prevents the browser from choking on
// massive datasets while still allowing enough points for meaningful charts.
// ---------------------------------------------------------------------------
const DEFAULT_ROW_LIMITS: Partial<Record<GraphType, number>> = {
  scatter: 1000,
  bar: 500,
  line: 2000,
  area: 2000,
};

/** Default number of pie slices before overflow is bucketed into "Other". */
export const DEFAULT_PIE_SLICE_LIMIT = 10;

// ---------------------------------------------------------------------------
// 1. Auto-sort date x-axis
// ---------------------------------------------------------------------------

/**
 * Returns an ORDER BY clause that sorts a date-typed x-axis ascending.
 * Skipped when the user already has an explicit sort on the same column.
 */
export const autoSortDateAxis = (
  xKey: string,
  columnType: DataType | undefined,
  existingSorts: RiftConfig["sorts"],
): string | null => {
  if (columnType !== "DATE") return null;

  const alreadySorted = existingSorts.some((s) => s.column === xKey);
  if (alreadySorted) return null;

  return `ORDER BY ${escapeIdentifier(xKey)} ASC`;
};

// ---------------------------------------------------------------------------
// 1b. Build chart ORDER BY from user sorts + auto-sort fallback
// ---------------------------------------------------------------------------

/**
 * Builds an ORDER BY clause for the chart outer query by combining user-defined
 * sorts (filtered to columns present in the output) with the auto-sort-date
 * fallback for the x-axis.
 *
 * @param existingSorts - User sorts from riftConfig
 * @param outputColumns - Set of column names present in the chart output
 * @param xKey - The x-axis column name
 * @param xType - The x-axis column data type
 * @returns An ORDER BY clause string, or null if no sorting applies
 */
export const buildChartOrderBy = (
  existingSorts: RiftConfig["sorts"],
  outputColumns: Set<string>,
  xKey: string,
  xType: DataType | undefined,
): string | null => {
  const applicableSorts = existingSorts.filter((s) =>
    outputColumns.has(s.column),
  );

  if (applicableSorts.length > 0) {
    const parts = applicableSorts.map(({ column, direction }) => {
      const nullsOrder = direction === "ASC" ? "NULLS FIRST" : "NULLS LAST";
      return `${escapeIdentifier(column)} ${direction} ${nullsOrder}`;
    });
    return `ORDER BY ${parts.join(", ")}`;
  }

  return autoSortDateAxis(xKey, xType, existingSorts);
};

// ---------------------------------------------------------------------------
// 2. Group cartesian x-axis to produce unique category values
// ---------------------------------------------------------------------------

/**
 * Context needed to decide how to aggregate y-columns when grouping the
 * x-axis for cartesian charts.
 */
export type CartesianGroupResult = {
  selectClause: string;
  groupByClause: string;
};

/**
 * Produces SELECT + GROUP BY clauses that deduplicate x-axis values and
 * aggregate y-axis columns with SUM (numbers) or COUNT(*) (others).
 *
 * - Bar charts always group — duplicate categories cause confusing stacking.
 * - Line/area/scatter only group when the rift config has no existing groups,
 *   since user-defined groups already handle deduplication.
 */
export const groupCartesianAxis = (
  xKey: string,
  yKeys: string[],
  columnDefs: Record<string, DataType> | null,
  chartType: GraphType,
  hasExistingGroups: boolean,
  columnsConfig?: ColumnsConfig | null,
): CartesianGroupResult | null => {
  // Only apply auto-grouping when there are no user-defined groups already.
  // Exception: bar charts always group because duplicate categories are very
  // confusing visually even when the user hasn't explicitly configured groups.
  if (hasExistingGroups && chartType !== "bar") return null;
  if (hasExistingGroups) return null;

  if (!yKeys.length) return null;

  const xEsc = escapeIdentifier(xKey);
  // Resolve the format the same way the UI does so an unset DATE x-axis still
  // buckets by its primitive default (day) on first load. A persisted format
  // always takes precedence over the type default.
  const xFmt = resolveColumnFormat(columnsConfig, xKey, columnDefs?.[xKey]);
  const xTruncUnit =
    xFmt.kind === "DATE" ? dateFormatToTruncUnit(xFmt.dateFormat) : null;
  const xTruncExpr = xTruncUnit
    ? `DATE_TRUNC('${xTruncUnit}', CAST(${xEsc} AS TIMESTAMP))`
    : null;
  const xGroupExpr = xTruncExpr ?? xEsc;
  // Emit the truncation as native TIMESTAMP (no outer CAST AS VARCHAR). Arrow
  // now stores DATE columns as a native temporal type, so DuckDB can propagate
  // timestamps through the chart rift without losing type fidelity.
  const xSelect = xTruncExpr ? `${xTruncExpr} AS ${xEsc}` : xEsc;

  const selectParts: string[] = [xSelect];

  for (const yKey of yKeys) {
    const yEsc = escapeIdentifier(yKey);
    // `columnDefs` here carries EFFECTIVE post-rift types (see chart-rift.ts),
    // so a text column already aggregated to a count is treated as numeric.
    const yType = columnDefs?.[yKey];

    // Numeric columns get SUM; everything else gets COUNT to produce a
    // meaningful numeric value the chart can render.
    const aggExpr = yType === "NUMBER" ? `SUM(${yEsc})` : `COUNT(${yEsc})`;
    selectParts.push(`${aggExpr} AS ${yEsc}`);
  }

  return {
    selectClause: selectParts.join(", "),
    groupByClause: `GROUP BY ${xGroupExpr}`,
  };
};

// ---------------------------------------------------------------------------
// 3. Pie chart auto-aggregation
// ---------------------------------------------------------------------------

export type PieAggResult = {
  /** Full SQL for the _pie_agg CTE body (SELECT ... FROM _base ...) */
  sql: string;
  /** Column name used as the angle (value) key in the aggregated result */
  angleAlias: string;
  /** Column name used as the sector label key in the aggregated result */
  sectorLabelAlias: string;
};

/**
 * Generates the pie aggregation CTE that groups by the label column and
 * aggregates the value column.
 *
 * When angleKey is numeric: SUM(angleKey) grouped by sectorLabelKey.
 * When angleKey is non-numeric (or missing): COUNT(*) grouped by sectorLabelKey.
 */
export const aggregatePie = (
  angleKey: string,
  sectorLabelKey: string,
  columnDefs: Record<string, DataType> | null,
): PieAggResult => {
  const labelEsc = escapeIdentifier(sectorLabelKey);
  // `columnDefs` here carries EFFECTIVE post-rift types (see chart-rift.ts), so
  // an already-aggregated count column is summed rather than re-counted.
  const angleType = columnDefs?.[angleKey];

  const angleEsc = escapeIdentifier(angleKey);
  const aggExpr = angleType === "NUMBER" ? `SUM(${angleEsc})` : "COUNT(*)";

  const whereClause = `WHERE ${labelEsc} IS NOT NULL AND CAST(${labelEsc} AS VARCHAR) != ''`;

  return {
    sql: `SELECT ${labelEsc} AS ${escapeIdentifier("sectorLabel")}, ${aggExpr} AS ${escapeIdentifier("angle")}\n  FROM _base\n  ${whereClause}\n  GROUP BY ${labelEsc}\n  ORDER BY ${escapeIdentifier("angle")} DESC`,
    angleAlias: "angle",
    sectorLabelAlias: "sectorLabel",
  };
};

// ---------------------------------------------------------------------------
// 4. Top-N + "Other" bucketing for pie charts
// ---------------------------------------------------------------------------

/**
 * Returns a SQL snippet that ranks pie slices and buckets everything beyond
 * `limit` into a single "Other" row. Operates on the `_pie_agg` CTE.
 *
 * Produces two CTEs:
 *   _pie_ranked — adds ROW_NUMBER
 *   Final SELECT — CASE WHEN for top-N vs "Other"
 */
export const bucketPieSlices = (
  limit: number,
  angleAlias: string,
  sectorLabelAlias: string,
): { rankedCte: string; finalSelect: string } => {
  const angleEsc = escapeIdentifier(angleAlias);
  const labelEsc = escapeIdentifier(sectorLabelAlias);

  const rankedCte = `SELECT *, ROW_NUMBER() OVER (ORDER BY ${angleEsc} DESC) AS _rn\n  FROM _pie_agg`;

  const caseExpr = `CASE WHEN _rn <= ${limit} THEN ${labelEsc} ELSE 'Other' END`;

  const finalSelect = `SELECT\n  ${caseExpr} AS ${labelEsc},\n  SUM(${angleEsc}) AS ${angleEsc}\nFROM _pie_ranked\nGROUP BY ${caseExpr}\nORDER BY SUM(${angleEsc}) DESC`;

  return { rankedCte, finalSelect };
};

// ---------------------------------------------------------------------------
// 5. Null / empty value filtering
// ---------------------------------------------------------------------------

/**
 * Returns a WHERE clause that filters out NULL and empty-string values for
 * the given columns. Used to prevent blank categories on chart axes.
 */
export const filterNulls = (keys: string[]): string | null => {
  if (!keys.length) return null;

  const conditions = keys.map((key) => {
    const esc = escapeIdentifier(key);
    return `${esc} IS NOT NULL AND CAST(${esc} AS VARCHAR) != ''`;
  });

  return `WHERE ${conditions.join(" AND ")}`;
};

// ---------------------------------------------------------------------------
// 6. Row limit for performance
// ---------------------------------------------------------------------------

/**
 * Returns a LIMIT clause when the user hasn't set one and the chart type has
 * a sensible default. Returns null if no limit should be applied (e.g. the
 * user already set one, or the chart type doesn't need limiting).
 */
export const applyRowLimit = (
  chartType: GraphType,
  existingLimit: number | null | undefined,
): string | null => {
  if (existingLimit != null && existingLimit > 0) return null;

  const defaultLimit = DEFAULT_ROW_LIMITS[chartType];
  if (!defaultLimit) return null;

  return `LIMIT ${defaultLimit}`;
};
